import React, { useState, useEffect, useRef } from 'react';
import './AboutPage.css';
import { COMPANY } from '../data/ItineraryData';
import logo from '../Gallery/logo.png';

/* ── Counter hook ── animates numbers on entry */
function useCounter(target, duration = 2000, started = false) {
    const [val, setVal] = useState(0);
    useEffect(() => {
        if (!started) return;
        let start = 0;
        const step = target / (duration / 16);
        const timer = setInterval(() => {
            start = Math.min(start + step, target);
            setVal(Math.floor(start));
            if (start >= target) clearInterval(timer);
        }, 16);
        return () => clearInterval(timer);
    }, [started, target, duration]);
    return val;
}

const STATS = [
    { label: 'Happy Travellers', value: 15000, suffix: '+', icon: 'fa-solid fa-users' },
    { label: 'Destinations Covered', value: 50, suffix: '+', icon: 'fa-solid fa-earth-asia' },
    { label: 'Years of Excellence', value: 30, suffix: '+', icon: 'fa-solid fa-award' },
    { label: 'Visa Processed', value: 3000, suffix: '+', icon: 'fa-solid fa-passport' },
];



const VALUES = [
    { icon: 'fa-solid fa-handshake', title: 'Trust', color: '#3e7e6a', desc: 'Every recommendation we make is driven by your best interest — not margins. We build relationships, not transactions.' },
    { icon: 'fa-solid fa-gem', title: 'Quality', color: '#5b21b6', desc: 'From hotel selection to guide quality, we vet every element of your journey with obsessive attention to detail.' },
    { icon: 'fa-solid fa-headset', title: 'Service', color: '#1e40af', desc: '24/7 availability for our travelling clients. Because emergencies abroad don\'t follow office hours.' },
    { icon: 'fa-solid fa-leaf', title: 'Responsibility', color: '#065f46', desc: 'We promote sustainable tourism — local stays, responsible wildlife experiences, and community-first travel.' },
    { icon: 'fa-solid fa-lightbulb', title: 'Innovation', color: '#b45309', desc: 'Digital itineraries, real-time visa tracking, and seamless online booking — travel done the modern way.' },
    { icon: 'fa-solid fa-heart', title: 'Passion', color: '#991b1b', desc: 'Every one of our team members is a passionate traveller first. We create experiences we would love ourselves.' },
];



/* ── Stat Counter with intersection observer ── */
const StatCard = ({ stat }) => {
    const ref = useRef();
    const [triggered, setTriggered] = useState(false);
    const count = useCounter(stat.value, 1800, triggered);

    useEffect(() => {
        const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setTriggered(true); }, { threshold: 0.5 });
        if (ref.current) obs.observe(ref.current);
        return () => obs.disconnect();
    }, []);

    return (
        <div className="ab-stat-card" ref={ref}>
            <div className="ab-stat-icon"><i className={stat.icon}></i></div>
            <div className="ab-stat-num">{count.toLocaleString()}{stat.suffix}</div>
            <div className="ab-stat-label">{stat.label}</div>
        </div>
    );
};

/* ════════════════════════════════════════════
   ABOUT PAGE
════════════════════════════════════════════ */
const AboutPage = () => {
    const [activeVal, setActiveVal] = useState(0);

    return (
        <div className="ab-page">

            {/* ══ 1. CINEMATIC HERO ══════════════════════════════════════ */}
            <section className="ab-hero">
                <div className="ab-hero-video-overlay" />
                <div className="ab-hero-img" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1488085061387-422e29b40080?w=1600&auto=format&fit=crop&q=80')" }} />
                <div className="ab-hero-content">
                    <img src={logo} alt="Silver Sky Holidays" className="ab-hero-logo" />
                    <div className="ab-hero-badge"><i className="fa-solid fa-passport"></i> Est. 1995 · Ahmedabad, India</div>
                    <h1 className="ab-hero-title">We Don't Just Plan<br /><span className="ab-hero-accent">Trips. We Create Memories.</span></h1>
                    <p className="ab-hero-sub">Gujarat's most trusted travel partner Crafting unforgettable journeys for families and travellers since 1995.</p>
                    <div className="ab-hero-pills">
                        {/* <span><i className="fa-solid fa-check"></i> IATA Approved</span> */}
                        {/* <span><i className="fa-solid fa-check"></i> MoT Certified</span> */}
                        <span><i className="fa-solid fa-check"></i> 15,000+ Clients</span>
                        <span><i className="fa-solid fa-check"></i> 50+ Countries</span>
                    </div>
                </div>
                <div className="ab-hero-scroll"><i className="fa-solid fa-chevron-down"></i></div>
            </section>

            {/* ══ 2. BRAND STORY — SPLIT LAYOUT ══════════════════════════ */}
            <section className="ab-story-section">
                <div className="ab-story-visual">
                    <div className="ab-story-img-main" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=800&auto=format&fit=crop&q=80')" }} />
                    <div className="ab-story-img-secondary" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=400&auto=format&fit=crop&q=80')" }} />
                    <div className="ab-story-badge-float">
                        <i className="fa-solid fa-award"></i>
                        <span>30+ Years<br />of Trust</span>
                    </div>
                </div>
                <div className="ab-story-text">
                    <div className="ab-section-label"><i className="fa-solid fa-book-open"></i> Our Story</div>
                    <h2 className="ab-section-title">Born from a Passion<br />for Exploration</h2>
                    <p className="ab-story-p">Silver Sky Holidays was founded in <strong>1995</strong> in Ahmedabad with a simple but powerful conviction: <strong>every traveller deserves a journey that is personally crafted, not mass-produced</strong>. From our very first booking to our fifteen-thousandth, that belief has never changed.</p>
                    <p className="ab-story-p">Over three decades, we have grown from a single-desk travel consultancy into one of Gujarat's most respected names in leisure and corporate travel — earning the trust of families who return year after year, passing the tradition of travel on to their children.</p>
                    <p className="ab-story-p">What sets us apart is not just experience — it is the care we bring to every detail. From visa applications processed in 48 hours to hand-picked hotels that genuinely reflect the destination, we treat every itinerary as if we are travelling ourselves.</p>
                    <div className="ab-story-cert-row">
                        <div className="ab-cert">
                            <i className="fa-solid fa-earth-asia"></i>
                            <div>
                                <strong>50+ Countries</strong>
                                <span>Destinations Covered</span>
                            </div>
                        </div>
                        <div className="ab-cert">
                            <i className="fa-solid fa-calendar-check"></i>
                            <div>
                                <strong>Est. 1995</strong>
                                <span>30+ Years in Travel</span>
                            </div>
                        </div>
                        <div className="ab-cert">
                            <i className="fa-solid fa-users"></i>
                            <div>
                                <strong>15,000+</strong>
                                <span>Happy Families</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ══ 3. ANIMATED STATS BAND ══════════════════════════════════ */}
            <section className="ab-stats-band">
                <div className="ab-stats-inner">
                    <div className="ab-stats-label">Our Impact in Numbers</div>
                    <div className="ab-stats-grid">
                        {STATS.map((s, i) => <StatCard key={i} stat={s} />)}
                    </div>
                </div>
            </section>



            {/* ══ 5. VALUES — INTERACTIVE GRID ════════════════════════════ */}
            <section className="ab-values-section">
                <div className="ab-section-inner">
                    <div className="ab-section-label centered" style={{ color: '#fff' }}><i className="fa-solid fa-compass"></i> What We Stand For</div>
                    <h2 className="ab-section-title centered" style={{ color: '#fff' }}>Our Core Values</h2>
                    <div className="ab-values-layout">
                        <div className="ab-values-list">
                            {VALUES.map((v, i) => (
                                <div key={i}
                                    className={`ab-val-item ${activeVal === i ? 'active' : ''}`}
                                    style={{ '--val-color': v.color }}
                                    onClick={() => setActiveVal(i)}>
                                    <div className="ab-val-icon"><i className={v.icon}></i></div>
                                    <div className="ab-val-name">{v.title}</div>
                                    <i className="fa-solid fa-chevron-right ab-val-arrow"></i>
                                </div>
                            ))}
                        </div>
                        <div className="ab-values-panel" style={{ '--val-color': VALUES[activeVal].color }}>
                            <div className="ab-vp-icon"><i className={VALUES[activeVal].icon}></i></div>
                            <h3 className="ab-vp-title">{VALUES[activeVal].title}</h3>
                            <p className="ab-vp-desc">{VALUES[activeVal].desc}</p>
                            <div className="ab-vp-decorator" />
                        </div>
                    </div>
                </div>
            </section>




            {/* ══ 8. WHY CHOOSE US — ASYMMETRIC GRID ══════════════════════ */}
            <section className="ab-why-section">
                <div className="ab-why-visual" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1530789253388-582c481c54b0?w=800&auto=format&fit=crop&q=80')" }}>
                    <div className="ab-why-visual-overlay" />
                    <div className="ab-why-visual-text">
                        <h3>Every journey begins<br />with a conversation.</h3>
                        <p>Call or WhatsApp us — we'll plan your perfect trip.</p>
                        <a href={`tel:${COMPANY.altPhone}`} className="ab-why-cta">
                            <i className="fa-solid fa-phone"></i> {COMPANY.altPhone}
                        </a>
                    </div>
                </div>
                <div className="ab-why-content">
                    <div className="ab-section-label"><i className="fa-solid fa-shield-halved"></i> Why Silver Sky</div>
                    <h2 className="ab-section-title">What Makes Us<br />Different</h2>
                    <div className="ab-why-points">
                        {[
                            { icon: 'fa-solid fa-magnifying-glass', title: 'Deeply Researched', desc: 'Every hotel, guide, and route is personally verified by our team — not just rated on an app.' },
                            { icon: 'fa-solid fa-indian-rupee-sign', title: 'No Hidden Costs', desc: 'Complete transparency in pricing. What we quote is what you pay. Period.' },
                            { icon: 'fa-solid fa-phone-volume', title: '24/7 Travel Desk', desc: 'Our operations team is always on standby for our clients on-trip — anywhere in the world.' },
                            { icon: 'fa-solid fa-sliders', title: 'Fully Customised', desc: 'No fixed templates. Every itinerary is built ground-up around your dates, budget, and interests.' },
                            { icon: 'fa-solid fa-shield-halved', title: 'Trusted & Certified', desc: 'we its self our trust and expierence of 30yrs in travel industry' },
                            { icon: 'fa-solid fa-rotate-left', title: 'Flexible Changes', desc: 'Plans change. We adapt. Amendments are handled with care, not contractual rigidity.' },
                        ].map((p, i) => (
                            <div key={i} className="ab-why-point">
                                <div className="ab-why-point-icon"><i className={p.icon}></i></div>
                                <div>
                                    <strong>{p.title}</strong>
                                    <p>{p.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ══ 9. TESTIMONIAL STRIP ════════════════════════════════════ */}
            <section className="ab-testi-section">
                <div className="ab-section-inner">
                    <div className="ab-section-label centered" style={{ color: '#fff' }}><i className="fa-solid fa-star"></i> What Our Clients Say</div>
                    <div className="ab-testi-grid">
                        {[
                            { name: 'Mehul & Ritu Joshi', dest: 'Bali, 2024', text: 'Rajesh bhai and the team made our honeymoon absolutely magical. Every detail was perfect — from the villa to the surprise candle-light dinner. Will only travel with Silver Sky from now on.', stars: 5 },
                            { name: 'Sharma Family', dest: 'Switzerland, 2023', text: 'Our family of 6 including elderly parents had the smoothest trip to Europe ever. Wheelchair access arrangements, special meals, everything was taken care of proactively. Outstanding!', stars: 5 },
                            { name: 'Darshna Patel', dest: 'Dubai, 2024', text: 'The visa was processed in 3 days, the hotel was stunning, and the desert safari was beyond expectations. I felt genuinely taken care of — not just another booking number.', stars: 5 },
                        ].map((t, i) => (
                            <div key={i} className="ab-testi-card">
                                <div className="ab-testi-stars">
                                    {[...Array(t.stars)].map((_, j) => <i key={j} className="fa-solid fa-star"></i>)}
                                </div>
                                <p className="ab-testi-text">"{t.text}"</p>
                                <div className="ab-testi-author">
                                    <div className="ab-testi-avatar"><i className="fa-solid fa-user"></i></div>
                                    <div>
                                        <div className="ab-testi-name">{t.name}</div>
                                        <div className="ab-testi-dest"><i className="fa-solid fa-location-dot"></i> {t.dest}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ══ 10. CONTACT CTA ════════════════════════════════════════ */}
            <section className="ab-cta-section">
                <div className="ab-cta-inner">
                    <div className="ab-cta-text">
                        <h2>Let's Plan Your Next Adventure</h2>
                        <p>Talk to our travel experts — Monday to Saturday, 9 AM to 8 PM IST</p>
                    </div>
                    <div className="ab-cta-actions">
                        <a href={`tel:${COMPANY.altPhone}`} className="ab-cta-btn ab-cta-primary">
                            <i className="fa-solid fa-phone"></i> {COMPANY.altPhone}
                        </a>
                        <a href={`mailto:${COMPANY.email}`} className="ab-cta-btn ab-cta-secondary">
                            <i className="fa-solid fa-envelope"></i> {COMPANY.email}
                        </a>
                        <div className="ab-cta-address">
                            <i className="fa-solid fa-location-dot"></i>
                            <span>{COMPANY.address}</span>
                        </div>
                    </div>
                </div>
            </section>

        </div>
    );
};

export default AboutPage;
